"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { platformFetch } from "@/lib/api/browser";
import { useIdentity } from "@/lib/api/session";
import { EMPTY_LOOK, type PlayerLook } from "./looks";

/* A player's look, read from and written to this casino (api/profile/look). */

const lookKey = (accountId: string | null) => ["profile-look", accountId] as const;

export const usePlayerLook = (accountId: string | null | undefined) =>
  useQuery({
    queryKey: lookKey(accountId ?? null),
    enabled: Boolean(accountId),
    staleTime: 60_000,
    queryFn: async (): Promise<PlayerLook> => {
      const res = await fetch(`/api/profile/look?accounts=${encodeURIComponent(accountId ?? "")}`, { cache: "no-store" });
      if (!res.ok) return EMPTY_LOOK;
      const body = (await res.json()) as { data?: Record<string, PlayerLook> };
      return body.data?.[accountId ?? ""] ?? EMPTY_LOOK;
    },
  });

/* The signed-in player's own look. */
export const useMyLook = () => {
  const { identity } = useIdentity();
  return usePlayerLook(identity?.id);
};

const putLook = (change: Partial<PlayerLook>) =>
  fetch("/api/profile/look", {
    method: "PUT",
    cache: "no-store",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(change),
  });

/* Where the platform keeps its session cookie on the API host, this casino's
   server cannot see who is signed in. The browser can: it fetches the
   platform's realtime URL and hands it over, and the server lets the platform
   vouch for the account before it opens a session here — the same proof the
   chat uses (api/chat/identity). A write refused as signed out runs it once
   and tries again. */
async function proveSession(accountId: string) {
  const realtimeUrl = (await platformFetch<{ data: { url: string } }>("realtime").catch(() => null))?.data?.url;
  if (!realtimeUrl) return false;
  const res = await fetch("/api/chat/identity", {
    method: "POST",
    cache: "no-store",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ realtimeUrl, accountId }),
  }).catch(() => null);
  return Boolean(res?.ok);
}

export const useSetMyLook = () => {
  const client = useQueryClient();
  const { identity } = useIdentity();
  const accountId = identity?.id ?? null;

  return useMutation({
    mutationFn: async (change: Partial<PlayerLook>): Promise<PlayerLook> => {
      let res = await putLook(change);
      if (res.status === 401 && accountId && (await proveSession(accountId))) res = await putLook(change);
      const body = (await res.json().catch(() => null)) as { data?: PlayerLook; error?: { message?: string } } | null;
      if (!res.ok || !body?.data) throw new Error(body?.error?.message ?? "Your profile could not be saved.");
      return body.data;
    },
    /* The new picture shows at once; a refusal puts the old one back. */
    onMutate: async (change) => {
      const key = lookKey(accountId);
      await client.cancelQueries({ queryKey: key });
      const previous = client.getQueryData<PlayerLook>(key);
      client.setQueryData<PlayerLook>(key, { ...EMPTY_LOOK, ...previous, ...change });
      return { previous };
    },
    onError: (_error, _change, context) => {
      client.setQueryData(lookKey(accountId), context?.previous ?? EMPTY_LOOK);
    },
    onSuccess: (look) => {
      client.setQueryData(lookKey(accountId), look);
    },
  });
};
